import { useEffect, type ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { MapContainer, TileLayer, Circle, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/** Zoom so the circle fits with comfortable padding. */
export function zoomForRadius(radiusMeters: number): number {
  if (radiusMeters <= 40) return 18;
  if (radiusMeters <= 80) return 17;
  if (radiusMeters <= 150) return 16;
  if (radiusMeters <= 300) return 15;
  if (radiusMeters <= 500) return 14;
  return 13;
}

const pinIcon = L.divIcon({
  className: 'geofence-pin',
  html: `<span style="
    display:block;width:14px;height:14px;border-radius:50%;
    background:#2563EB;border:2px solid #fff;
    box-shadow:0 1px 4px rgba(15,23,42,0.35);
  "></span>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

interface GeofenceMapProps {
  latitude: number;
  longitude: number;
  radius: number;
  height?: number | string;
  interactive?: boolean;
  showRadiusLabel?: boolean;
  /** Kept for API compatibility; unused with Leaflet */
  mapInstanceId?: string;
  wrapProvider?: boolean;
}

function MapCameraSync({
  latitude,
  longitude,
  zoom,
}: {
  latitude: number;
  longitude: number;
  zoom: number;
}) {
  const map = useMap();
  useEffect(() => {
    map.setView([latitude, longitude], zoom, { animate: false });
  }, [map, latitude, longitude, zoom]);

  useEffect(() => {
    const id = window.setTimeout(() => map.invalidateSize(), 50);
    return () => window.clearTimeout(id);
  }, [map]);

  return null;
}

/**
 * OpenStreetMap (Leaflet) preview of a circular geofence — no Google Maps billing.
 */
export function GeofenceMap({
  latitude,
  longitude,
  radius,
  height = 140,
  interactive = false,
  showRadiusLabel = true,
}: GeofenceMapProps) {
  const safeRadius = Number.isFinite(radius) && radius > 0 ? radius : 100;
  const zoom = zoomForRadius(safeRadius);
  const center: [number, number] = [latitude, longitude];

  return (
    <Box
      sx={{
        width: '100%',
        height,
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        position: 'relative',
        bgcolor: 'neutral.50',
        '& .leaflet-container': {
          width: '100%',
          height: '100%',
          fontFamily: 'inherit',
          background: '#e2e8f0',
        },
        '& .geofence-pin': {
          background: 'transparent',
          border: 'none',
        },
      }}
    >
      <MapContainer
        center={center}
        zoom={zoom}
        zoomControl={interactive}
        attributionControl
        dragging={interactive}
        scrollWheelZoom={interactive}
        doubleClickZoom={interactive}
        touchZoom={interactive}
        boxZoom={interactive}
        keyboard={interactive}
        style={{ width: '100%', height: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapCameraSync latitude={latitude} longitude={longitude} zoom={zoom} />
        <Circle
          center={center}
          radius={safeRadius}
          pathOptions={{
            color: '#2563EB',
            weight: 2,
            opacity: 0.9,
            fillColor: '#2563EB',
            fillOpacity: 0.18,
          }}
        />
        <Marker position={center} icon={pinIcon} />
      </MapContainer>

      {showRadiusLabel && (
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{
            position: 'absolute',
            bottom: 10,
            right: 12,
            color: 'text.primary',
            bgcolor: 'background.paper',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            boxShadow: 1,
            zIndex: 1000,
          }}
        >
          {safeRadius}m radius
        </Typography>
      )}
    </Box>
  );
}

/** No-op provider — Leaflet needs no API key wrapper. Kept for call-site compatibility. */
export function GeofenceMapProvider({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
